/**
 * Scanned Book Reader (Simplified v8.4)
 * 
 * Parents see only published, quality-assured books.
 * All complexity moved to agent-side (human-in-the-loop model):
 * - Turn Art generated by agents
 * - Digital page breaks defined by agents
 * - Quality ensured before publishing
 * 
 * Child-aware reading:
 * - Extracts childId from URL query param
 * - Fetches child's current learning skill
 * - Filters word highlighting based on skill level
 * 
 * Uses BookReadingView for core rendering (DRY).
 * 
 * @see archive/parent-reader-legacy/ for the old 1500-line version
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Layers, Loader2, Type, User } from 'lucide-react';
import { typography } from '@/lib/design';
import { TurnColorMapping, STANDARD_COLORS, type TurnCodeError } from '@/lib/turn-color';
import { BookReadingView, type ReadingPage } from '@/components/shared';
import { errorTracker } from '@/lib/logging';
import { supabase } from '@/lib/supabase/client';
import {
  getScannedBook,
  getDigitalPages,
  getColorMappings,
  ScannedBook,
  DigitalPage,
} from '@/services/scannedBookService';
import { getCurrentLearningSkill, getChildSkillProgress } from '@/lib/voice-agent/skill-engine';
import type { CurrentSkill } from '@/lib/voice-agent/types';
import { recordReadingSession, type WordExposure } from '@/services/learningProgressService';

// ============================================================================
// Reader Settings
// ============================================================================

interface ReaderSettings {
  fontSize: number; // 20-52 px
  lineSpacing: number; // 1.4-2.4
}

const DEFAULT_SETTINGS: ReaderSettings = {
  fontSize: 32,
  lineSpacing: 1.4,
};

function loadSettings(): ReaderSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  
  try {
    const saved = localStorage.getItem('turn-reader-settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old values
      if (typeof parsed.fontSize === 'string') {
        parsed.fontSize = 32;
      }
      if (typeof parsed.lineSpacing === 'string' || parsed.lineSpacing > 1.6) {
        parsed.lineSpacing = 1.4;
      }
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {}
  
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: ReaderSettings) {
  localStorage.setItem('turn-reader-settings', JSON.stringify(settings));
}

// ============================================================================
// Types
// ============================================================================

interface ChildInfo {
  id: string;
  name: string;
  in_color_program: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ScannedBookReader: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Child context from URL
  const childId = searchParams.get('child');

  // Core state
  const [book, setBook] = useState<ScannedBook | null>(null);
  const [pages, setPages] = useState<ReadingPage[]>([]);
  const [colorMappings, setColorMappings] = useState<TurnColorMapping[]>(STANDARD_COLORS);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  
  // Child-aware state
  const [child, setChild] = useState<ChildInfo | null>(null);
  const [currentSkill, setCurrentSkill] = useState<CurrentSkill | null>(null);
  const [activeSkills, setActiveSkills] = useState<string[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  
  // Settings
  const [settings, setSettings] = useState<ReaderSettings>(loadSettings);
  
  // Session tracking for progress analytics
  const sessionStartTime = useRef<Date>(new Date());
  const pagesViewed = useRef<Set<number>>(new Set());
  const wordsExposed = useRef<Map<string, WordExposure>>(new Map());
  const sessionSaved = useRef<boolean>(false);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadBook = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Load book metadata, digital pages, and color mappings in parallel
      const [bookData, digitalPages, colors] = await Promise.all([
        getScannedBook(id),
        getDigitalPages(id),
        getColorMappings(),
      ]);

      if (!bookData) {
        setError('Book not found');
        return;
      }

      setBook(bookData);
      // Only update if we got data; preserve STANDARD_COLORS fallback on empty/error
      if (colors.length) setColorMappings(colors);

      // If no digital pages exist, show error (agents should have created them)
      if (digitalPages.length === 0) {
        setError('This book is not ready for reading yet');
        return;
      }

      // Transform DigitalPage[] to ReadingPage[] for BookReadingView
      const readingPages: ReadingPage[] = digitalPages.map(dp => ({
        text: dp.sentenceText || '',
        turnArtUrl: dp.turnArtUrl || null,
        words: dp.words || [],
      }));

      setPages(readingPages);
      
    } catch (err) {
      console.error('[Reader] Load error:', err);
      setError('Failed to load book');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load child info and current skill
  const loadChildContext = useCallback(async () => {
    if (!childId) return;

    try {
      // Load child info (cast needed: in_color_program is newly added)
      const { data: childData } = await supabase
        .from('children')
        .select('id, name, in_color_program')
        .eq('id', childId)
        .maybeSingle() as unknown as { data: ChildInfo | null; error: any };

      if (childData) {
        setChild(childData);

        if (childData.in_color_program === false) {
          // Graduated from Color Program — render plain text
          setActiveSkills([]);
          console.log('[Reader] Child graduated from Color Program, no turn colors:', { childId, childName: childData.name });
        } else {
          // Load child's current learning skill (for header badge display)
          const skill = await getCurrentLearningSkill(childId);
          setCurrentSkill(skill);

          // Load ALL active skills (learning + mastered) for Turn Color filtering
          const progress = await getChildSkillProgress(childId);
          const activeSkillIds = progress
            .filter(p => p.status === 'learning' || p.status === 'mastered')
            .map(p => p.skill_id);
          setActiveSkills(activeSkillIds);

          console.log('[Reader] Child context loaded:', {
            childId,
            childName: childData.name,
            currentSkill: skill?.skillId,
            activeSkills: activeSkillIds.length,
          });
        }
      }
    } catch (err) {
      console.error('[Reader] Error loading child context:', err);
      // Non-fatal - continue without child context
    }
  }, [childId]);

  useEffect(() => {
    loadBook();
  }, [loadBook]);

  useEffect(() => {
    loadChildContext();
  }, [loadChildContext]);

  // Save settings when they change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Track page views and word exposures
  useEffect(() => {
    if (!pages.length || currentPageIndex < 0) return;
    
    // Mark current page as viewed
    pagesViewed.current.add(currentPageIndex);
    
    // Track words on this page
    const currentPage = pages[currentPageIndex];
    if (currentPage?.words) {
      currentPage.words.forEach(word => {
        if (word.turnCode && word.inLexicon) {
          // Use word as key since we may not have word IDs
          const key = word.word.toLowerCase();
          if (!wordsExposed.current.has(key)) {
            wordsExposed.current.set(key, {
              wordId: key, // Will be resolved to actual ID by service
              word: word.word,
              skillId: currentSkill?.skillId,
            });
          }
        }
      });
    }
  }, [currentPageIndex, pages, currentSkill?.skillId]);

  // Save reading session on unmount or navigation
  const saveSession = useCallback(async () => {
    // Only save once and only if we have a child and book
    if (sessionSaved.current || !childId || !book || !id) return;
    
    // Only save if they actually read something
    if (pagesViewed.current.size === 0) return;
    
    const durationSeconds = Math.round(
      (new Date().getTime() - sessionStartTime.current.getTime()) / 1000
    );
    
    // Minimum 10 seconds to count as a session
    // IMPORTANT: Check duration BEFORE setting sessionSaved flag to avoid
    // losing sessions when user switches tabs/apps within 10 seconds
    if (durationSeconds < 10) return;
    
    sessionSaved.current = true;
    
    console.log('[Reader] Saving reading session:', {
      childId,
      bookId: id,
      pagesRead: pagesViewed.current.size,
      totalPages: pages.length,
      wordsExposed: wordsExposed.current.size,
      durationSeconds,
    });
    
    try {
      await recordReadingSession(
        childId,
        id,
        book.title,
        durationSeconds,
        pagesViewed.current.size,
        pages.length,
        Array.from(wordsExposed.current.values()),
        currentSkill?.skillId
      );
      console.log('[Reader] Reading session saved successfully');
    } catch (error) {
      console.error('[Reader] Error saving reading session:', error);
    }
  }, [childId, book, id, pages.length, currentSkill?.skillId]);

  // Save session on unmount
  useEffect(() => {
    return () => {
      saveSession();
    };
  }, [saveSession]);

  // Also save on visibility change (user switches tabs/apps)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveSession();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveSession]);

  // ============================================================================
  // Navigation
  // ============================================================================

  const goBack = useCallback(() => {
    // Preserve child context when going back
    const backUrl = childId ? `/read?child=${childId}` : '/read';
    navigate(backUrl);
  }, [navigate, childId]);

  // Toggle controls visibility on center tap
  const handleCenterTap = useCallback(() => {
    setShowControls(prev => !prev);
  }, []);

  // Turn Code error handler - silent logging for parent reader
  // (Parents shouldn't see fix UI, but we need to track data quality issues)
  const reportedErrorsRef = useRef<Set<string>>(new Set());
  const handleTurnCodeError = useCallback((error: TurnCodeError) => {
    const errorKey = `${error.word}:${error.turnCode}`;
    if (reportedErrorsRef.current.has(errorKey)) return;
    reportedErrorsRef.current.add(errorKey);
    
    errorTracker.dataIntegrity(
      `Invalid turn_code for "${error.word}": ${error.error}`,
      'turn_color_words',
      { metadata: { word: error.word, turnCode: error.turnCode, extractedLetters: error.extractedLetters } }
    );
  }, []);

  // Escape key to show controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowControls(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5efe6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#5C2D91] mx-auto mb-4" />
          <p className="text-[#8E8E93]">Loading book...</p>
        </div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-[#f5efe6] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-[#C7C7CC] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">{error || 'Book not found'}</h2>
          <button 
            onClick={goBack} 
            className="mt-4 px-6 py-2 bg-[#0A84FF] text-white rounded-lg hover:brightness-110 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="min-h-screen bg-[#f5efe6] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-[#C7C7CC] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">No pages available</h2>
          <p className="text-[#8E8E93] mb-4">This book hasn't been published yet.</p>
          <button 
            onClick={goBack} 
            className="px-6 py-2 bg-[#0A84FF] text-white rounded-lg hover:brightness-110 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-[#f5efe6] relative select-none overflow-hidden safe-area-pt safe-area-pb">
      {/* Header - fixed, themed for book paper aesthetic */}
      <header 
        className={`fixed top-0 left-0 right-0 bg-[#f5efe6]/95 backdrop-blur-sm border-b border-[#e0d5c5] z-30 transition-transform duration-300 ${
          showControls ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="px-4 py-3 flex items-center gap-4">
          {/* Back button */}
          <button
            onClick={goBack}
            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#e0d5c5] transition-colors shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-[#6b5d4d]" />
          </button>

          {/* Title + page info */}
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-[#3d3225] truncate text-sm">{book.title}</h1>
            <div className="flex items-center gap-2 text-xs text-[#8b7c6b]">
              <span>{currentPageIndex + 1} / {pages.length}</span>
              {child && (
                <>
                  <span className="text-[#c0b5a5]">•</span>
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {child.name}
                  </span>
                </>
              )}
              {currentSkill && (
                <span 
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{ 
                    backgroundColor: `${currentSkill.color}20`,
                    color: currentSkill.color,
                  }}
                >
                  {currentSkill.soundPattern || currentSkill.skillId}
                </span>
              )}
            </div>
          </div>

          {/* Practice Flashcards */}
          {childId && (
            <button
              onClick={() => navigate(`/library/book/${id}/flashcards?child=${childId}`)}
              className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#e0d5c5] transition-colors shrink-0"
              aria-label="Practice flashcards"
              title="Practice Flashcards"
            >
              <Layers className="w-5 h-5 text-[#6b5d4d]" />
            </button>
          )}

          {/* Font size control */}
          <div className="flex items-center gap-2 shrink-0">
            <Type className="w-3.5 h-3.5 text-[#8b7c6b]" />
            <input
              type="range"
              min="20"
              max="52"
              step="4"
              value={settings.fontSize}
              onChange={(e) => setSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
              className="w-20 h-1.5 bg-[#ddd3c3] rounded-full appearance-none cursor-pointer accent-[#8b7355]"
              aria-label="Font size"
            />
            <Type className="w-4.5 h-4.5 text-[#6b5d4d]" />
          </div>
        </div>
      </header>

      {/* Reading area - BookReadingView handles everything */}
      {/* Padding top accounts for fixed header when visible (~60px) */}
      <main 
        className={`h-full overflow-hidden transition-[padding] duration-300 ${showControls ? 'pt-[60px]' : 'pt-0'}`}
        onClick={(e) => {
          // Center tap toggles controls
          const rect = e.currentTarget.getBoundingClientRect();
          const relativeX = (e.clientX - rect.left) / rect.width;
          if (relativeX > 0.25 && relativeX < 0.75) {
            handleCenterTap();
          }
        }}
      >
        <BookReadingView
          pages={pages}
          currentPageIndex={currentPageIndex}
          onPageChange={setCurrentPageIndex}
          colorMappings={colorMappings}
          phase={2}
          fontSize={settings.fontSize}
          lineSpacing={settings.lineSpacing}
          showNavigation={true}
          showProgress={true}
          enableKeyboard={true}
          enableTouch={true}
          height="100%"
          theme="light"
          mode="text-only"
          skillFilter={activeSkills}
          onTurnCodeError={handleTurnCodeError}
        />
      </main>
    </div>
  );
};

export default ScannedBookReader;
